import { FloodGame } from '../../games/flood/FloodGame.js';
import { FloodVortex } from '../../games/flood/FloodVortex.js';
import { FloodRenderer } from '../../games/flood/FloodRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Flood-specific types ──────────────────────────────────────────

export interface FloodRenderOptions {
    // Placeholder — FloodRenderer is fully game-state driven
}

// ── Adapter ───────────────────────────────────────────────────────

export class FloodTutorialAdapter implements TutorialGameAdapter {
    private game: FloodGame;
    private renderer: FloodRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new FloodGame(grid);
        this.renderer = new FloodRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        if (typeof state !== 'string') return;

        let serialized = state;
        let maxTime: number | null = null;

        // Parse @N suffix for simulate-until
        const atIndex = serialized.lastIndexOf('@');
        if (atIndex >= 0) {
            maxTime = parseFloat(serialized.substring(atIndex + 1));
            serialized = serialized.substring(0, atIndex);
        }

        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }

        if (maxTime !== null) {
            this.game.limitDisplayTime(maxTime);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createFloodTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new FloodTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface FloodVortexTutorialState {
    flood: string;
    gateMode?: boolean;
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class FloodVortexTutorialAdapter implements TutorialGameAdapter {
    private game: FloodVortex;
    private renderer: FloodRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new FloodVortex(grid);
        this.renderer = new FloodRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as FloodVortexTutorialState | null;
        this.game.reset();
        this.game.setVortexData(this.gatePairs, this.allGates);
        if (s?.flood) {
            this.game.deserialize(s.flood);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createFloodVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex flood tutorial: C0 ↔ B3
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 0, col: 2 }, { row: 3, col: 1 }],
    ];
    return new FloodVortexTutorialAdapter(canvas, grid, gatePairs);
}
